import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type PointerEvent as ReactPointerEvent,
  type RefObject,
} from 'react';
import { clientPointToElement } from './designPointer';

export type PanCamera = {
  x: number;
  y: number;
  scale: number;
};

export type UseMapPanOptions = {
  getCamera: () => PanCamera;
  onPan: (camera: PanCamera) => void;
  enabled?: boolean;
  /** CSS-px movement before a pan starts (avoids stealing taps). */
  activationThreshold?: number;
  minScale: number;
  maxScale: number;
  /** Ref to the viewport element (needed for non-passive wheel). */
  viewportRef: RefObject<HTMLElement | null>;
};

const DEFAULT_ACTIVATION = 10;
/** Exponential wheel zoom: larger deltaY → stronger, still gradual. */
const WHEEL_ZOOM_SENSITIVITY = 0.00135;
/** Per-frame blend toward the wheel target (0–1). */
const WHEEL_ZOOM_LERP = 0.22;
const WHEEL_ZOOM_EPSILON = 0.00015;
/** Arrow / programmatic pan: same feel as the old CSS ease-out. */
const NUDGE_DURATION_MS = 350;

function easeOutCubic(t: number): number {
  return 1 - (1 - t) ** 3;
}

type PointerSample = {
  pointerId: number;
  clientX: number;
  clientY: number;
};

type PanSession = {
  pointerId: number;
  startClientX: number;
  startClientY: number;
  startCamera: PanCamera;
  active: boolean;
};

type PinchSession = {
  startDistance: number;
  startScale: number;
  startCamera: PanCamera;
  startFocalX: number;
  startFocalY: number;
};

type NudgeAnim = {
  startX: number;
  startY: number;
  startScale: number;
  targetX: number;
  targetY: number;
  startedAt: number;
  rafId: number | null;
};

function clamp(value: number, min: number, max: number) {
  return Math.min(max, Math.max(min, value));
}

function pointerDistance(a: PointerSample, b: PointerSample): number {
  return Math.hypot(a.clientX - b.clientX, a.clientY - b.clientY);
}

function zoomAt(
  camera: PanCamera,
  nextScale: number,
  focalX: number,
  focalY: number,
  minScale: number,
  maxScale: number,
): PanCamera {
  const scale = clamp(nextScale, minScale, maxScale);
  if (scale === camera.scale) {
    return camera;
  }
  const worldX = (camera.x + focalX) / camera.scale;
  const worldY = (camera.y + focalY) / camera.scale;
  return {
    x: worldX * scale - focalX,
    y: worldY * scale - focalY,
    scale,
  };
}

/**
 * Continuous 2D pan + pinch / wheel zoom for the map viewport.
 */
export function useMapPan({
  getCamera,
  onPan,
  enabled = true,
  activationThreshold = DEFAULT_ACTIVATION,
  minScale,
  maxScale,
  viewportRef,
}: UseMapPanOptions) {
  const pointersRef = useRef<Map<number, PointerSample>>(new Map());
  const panSessionRef = useRef<PanSession | null>(null);
  const pinchSessionRef = useRef<PinchSession | null>(null);
  const getCameraRef = useRef(getCamera);
  const onPanRef = useRef(onPan);
  const minScaleRef = useRef(minScale);
  const maxScaleRef = useRef(maxScale);
  getCameraRef.current = getCamera;
  onPanRef.current = onPan;
  minScaleRef.current = minScale;
  maxScaleRef.current = maxScale;
  const [isDragging, setIsDragging] = useState(false);
  const wheelAnimRef = useRef<{
    targetScale: number;
    focalX: number;
    focalY: number;
    rafId: number | null;
  } | null>(null);
  const nudgeAnimRef = useRef<NudgeAnim | null>(null);
  const tickNudgeRef = useRef<() => void>(() => {});

  const clearGestureState = useCallback(() => {
    panSessionRef.current = null;
    pinchSessionRef.current = null;
    setIsDragging(false);
  }, []);

  const stopWheelAnimation = useCallback(() => {
    const anim = wheelAnimRef.current;
    if (anim?.rafId != null) {
      cancelAnimationFrame(anim.rafId);
    }
    wheelAnimRef.current = null;
  }, []);

  const stopNudgeAnimation = useCallback(() => {
    const anim = nudgeAnimRef.current;
    if (anim?.rafId != null) {
      cancelAnimationFrame(anim.rafId);
    }
    nudgeAnimRef.current = null;
  }, []);

  tickNudgeRef.current = () => {
    const anim = nudgeAnimRef.current;
    if (!anim) {
      return;
    }
    const t = Math.min(1, (performance.now() - anim.startedAt) / NUDGE_DURATION_MS);
    const eased = easeOutCubic(t);
    onPanRef.current({
      x: anim.startX + (anim.targetX - anim.startX) * eased,
      y: anim.startY + (anim.targetY - anim.startY) * eased,
      scale: anim.startScale,
    });
    if (t < 1) {
      anim.rafId = requestAnimationFrame(() => {
        tickNudgeRef.current();
      });
      return;
    }
    anim.rafId = null;
    nudgeAnimRef.current = null;
    if (
      pointersRef.current.size === 0 &&
      panSessionRef.current == null &&
      pinchSessionRef.current == null &&
      wheelAnimRef.current == null
    ) {
      setIsDragging(false);
    }
  };

  const nudgeBy = useCallback(
    (delta: { dx: number; dy: number }) => {
      if (!enabled || (delta.dx === 0 && delta.dy === 0)) {
        return;
      }
      stopWheelAnimation();
      const camera = getCameraRef.current();
      stopNudgeAnimation();
      setIsDragging(true);
      const anim: NudgeAnim = {
        startX: camera.x,
        startY: camera.y,
        startScale: camera.scale,
        targetX: camera.x + delta.dx,
        targetY: camera.y + delta.dy,
        startedAt: performance.now(),
        rafId: null,
      };
      nudgeAnimRef.current = anim;
      anim.rafId = requestAnimationFrame(() => {
        tickNudgeRef.current();
      });
    },
    [enabled, stopNudgeAnimation, stopWheelAnimation],
  );

  useEffect(() => {
    if (!enabled) {
      stopNudgeAnimation();
      setIsDragging(false);
    }
  }, [enabled, stopNudgeAnimation]);

  useEffect(() => {
    return () => {
      stopNudgeAnimation();
    };
  }, [stopNudgeAnimation]);

  const beginPinch = useCallback((element: Element) => {
    const points = [...pointersRef.current.values()];
    if (points.length < 2) {
      return;
    }
    const [a, b] = points;
    const distance = pointerDistance(a, b);
    if (distance <= 0) {
      return;
    }
    stopWheelAnimation();
    stopNudgeAnimation();
    const mid = clientPointToElement(
      element,
      (a.clientX + b.clientX) / 2,
      (a.clientY + b.clientY) / 2,
    );
    const camera = getCameraRef.current();
    panSessionRef.current = null;
    pinchSessionRef.current = {
      startDistance: distance,
      startScale: camera.scale,
      startCamera: camera,
      startFocalX: mid.x,
      startFocalY: mid.y,
    };
    setIsDragging(true);
  }, [stopNudgeAnimation, stopWheelAnimation]);

  const onPointerDown = useCallback(
    (event: ReactPointerEvent<HTMLElement>) => {
      if (!enabled || event.button !== 0) {
        return;
      }
      const target = event.target;
      if (
        target instanceof Element &&
        target.closest('button, a, input, textarea, select, [role="button"]')
      ) {
        return;
      }

      pointersRef.current.set(event.pointerId, {
        pointerId: event.pointerId,
        clientX: event.clientX,
        clientY: event.clientY,
      });

      if (pointersRef.current.size >= 2) {
        beginPinch(event.currentTarget);
        return;
      }

      pinchSessionRef.current = null;
      stopNudgeAnimation();
      panSessionRef.current = {
        pointerId: event.pointerId,
        startClientX: event.clientX,
        startClientY: event.clientY,
        startCamera: getCameraRef.current(),
        active: false,
      };
    },
    [beginPinch, enabled, stopNudgeAnimation],
  );

  const onPointerMove = useCallback(
    (event: ReactPointerEvent<HTMLElement>) => {
      if (!enabled || !pointersRef.current.has(event.pointerId)) {
        return;
      }

      pointersRef.current.set(event.pointerId, {
        pointerId: event.pointerId,
        clientX: event.clientX,
        clientY: event.clientY,
      });

      const pinch = pinchSessionRef.current;
      if (pinch && pointersRef.current.size >= 2) {
        const points = [...pointersRef.current.values()];
        const [a, b] = points;
        const distance = pointerDistance(a, b);
        if (distance <= 0 || pinch.startDistance <= 0) {
          return;
        }
        const mid = clientPointToElement(
          event.currentTarget,
          (a.clientX + b.clientX) / 2,
          (a.clientY + b.clientY) / 2,
        );
        const nextScale =
          pinch.startScale * (distance / pinch.startDistance);
        // Pan with the midpoint while pinching (focal follows fingers).
        const scale = clamp(
          nextScale,
          minScaleRef.current,
          maxScaleRef.current,
        );
        const worldX =
          (pinch.startCamera.x + pinch.startFocalX) / pinch.startCamera.scale;
        const worldY =
          (pinch.startCamera.y + pinch.startFocalY) / pinch.startCamera.scale;
        onPanRef.current({
          x: worldX * scale - mid.x,
          y: worldY * scale - mid.y,
          scale,
        });
        return;
      }

      const session = panSessionRef.current;
      if (!session || session.pointerId !== event.pointerId) {
        return;
      }

      const dx = event.clientX - session.startClientX;
      const dy = event.clientY - session.startClientY;

      if (!session.active) {
        if (Math.hypot(dx, dy) < activationThreshold) {
          return;
        }
        stopWheelAnimation();
        stopNudgeAnimation();
        session.active = true;
        setIsDragging(true);
        event.currentTarget.setPointerCapture(event.pointerId);
      }

      onPanRef.current({
        x: session.startCamera.x - dx,
        y: session.startCamera.y - dy,
        scale: session.startCamera.scale,
      });
    },
    [activationThreshold, enabled, stopNudgeAnimation, stopWheelAnimation],
  );

  const endPointer = useCallback(
    (event: ReactPointerEvent<HTMLElement>) => {
      if (!pointersRef.current.has(event.pointerId)) {
        return;
      }

      const session = panSessionRef.current;
      if (
        session?.pointerId === event.pointerId &&
        session.active &&
        event.currentTarget.hasPointerCapture(event.pointerId)
      ) {
        event.currentTarget.releasePointerCapture(event.pointerId);
      }

      pointersRef.current.delete(event.pointerId);

      if (pointersRef.current.size >= 2) {
        beginPinch(event.currentTarget);
        return;
      }

      pinchSessionRef.current = null;

      if (pointersRef.current.size === 1) {
        const remaining = [...pointersRef.current.values()][0];
        panSessionRef.current = {
          pointerId: remaining.pointerId,
          startClientX: remaining.clientX,
          startClientY: remaining.clientY,
          startCamera: getCameraRef.current(),
          active: false,
        };
        setIsDragging(false);
        return;
      }

      clearGestureState();
    },
    [beginPinch, clearGestureState],
  );

  useEffect(() => {
    const element = viewportRef.current;
    if (!element || !enabled) {
      return;
    }

    const tickWheelZoom = () => {
      const anim = wheelAnimRef.current;
      if (!anim) {
        return;
      }

      const camera = getCameraRef.current();
      const nextScale =
        camera.scale + (anim.targetScale - camera.scale) * WHEEL_ZOOM_LERP;
      const settled =
        Math.abs(anim.targetScale - nextScale) < WHEEL_ZOOM_EPSILON ||
        nextScale === minScaleRef.current ||
        nextScale === maxScaleRef.current;

      const scale = settled ? anim.targetScale : nextScale;
      const next = zoomAt(
        camera,
        scale,
        anim.focalX,
        anim.focalY,
        minScaleRef.current,
        maxScaleRef.current,
      );

      if (next.scale !== camera.scale || next.x !== camera.x || next.y !== camera.y) {
        onPanRef.current(next);
      }

      if (settled || next.scale === camera.scale) {
        anim.rafId = null;
        wheelAnimRef.current = null;
        if (
          pointersRef.current.size === 0 &&
          panSessionRef.current == null &&
          pinchSessionRef.current == null
        ) {
          setIsDragging(false);
        }
        return;
      }

      anim.rafId = requestAnimationFrame(tickWheelZoom);
    };

    const onWheel = (event: WheelEvent) => {
      event.preventDefault();
      if (pinchSessionRef.current != null || pointersRef.current.size >= 2) {
        return;
      }

      const camera = getCameraRef.current();
      const rect = element.getBoundingClientRect();
      const focal = clientPointToElement(element, event.clientX, event.clientY);
      const focalX = clamp(focal.x, 0, rect.width);
      const focalY = clamp(focal.y, 0, rect.height);

      // Normalize line/page deltas so mouse notches and trackpads feel similar.
      let delta = event.deltaY;
      if (event.deltaMode === 1) {
        delta *= 16;
      } else if (event.deltaMode === 2) {
        delta *= rect.height;
      }

      const existing = wheelAnimRef.current;
      const fromScale = existing?.targetScale ?? camera.scale;
      const targetScale = clamp(
        fromScale * Math.exp(-delta * WHEEL_ZOOM_SENSITIVITY),
        minScaleRef.current,
        maxScaleRef.current,
      );

      if (Math.abs(targetScale - camera.scale) < WHEEL_ZOOM_EPSILON && !existing) {
        return;
      }

      stopNudgeAnimation();
      setIsDragging(true);
      if (existing) {
        existing.targetScale = targetScale;
        existing.focalX = focalX;
        existing.focalY = focalY;
        if (existing.rafId == null) {
          existing.rafId = requestAnimationFrame(tickWheelZoom);
        }
        return;
      }

      wheelAnimRef.current = {
        targetScale,
        focalX,
        focalY,
        rafId: requestAnimationFrame(tickWheelZoom),
      };
    };

    element.addEventListener('wheel', onWheel, { passive: false });
    return () => {
      element.removeEventListener('wheel', onWheel);
      stopWheelAnimation();
    };
  }, [enabled, stopNudgeAnimation, stopWheelAnimation, viewportRef]);

  return {
    isDragging,
    nudgeBy,
    onPointerDown,
    onPointerMove,
    onPointerUp: endPointer,
    onPointerCancel: endPointer,
    style: { touchAction: 'none' as const },
  };
}
